import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
const classNames = require('classnames');
import FileList, { IMAGE_EXTENSIONS, getFileExtension } from './fileList'
import { shortFileName, getAxiosError, uploadFileOnPublicApiV2 } from '../../common/util';
import {
    ATTACH_FILES,
    ATTACH_WORKKARD_FILES,
    ATTACH_WORKKARD_LETTER,
    VALID_FILE,
    INVALID_FILE_SIZE,
    INVALID_FILE_EXTENSION,
    INVALID_FILE_EXTENSION_ERROR,
    INVALID_FILE_SIZE_ERROR,
    SERVER_UNAVAILABLE_ERROR_MESSAGE,
    MALICIOUS_OR_VIRUS_INFECTED_ERROR,
} from './constants';
import Icons from '../../icons';
import { size } from 'lodash';

import "./styles.scss";

const isUploadComplete = (item) => !item.hasOwnProperty('isLoading') || item.isLoading == false;
const isImageFile = (fileName) => IMAGE_EXTENSIONS.has(getFileExtension(fileName));

const getErrorIconName = (errorText) => {
    if (errorText == MALICIOUS_OR_VIRUS_INFECTED_ERROR) {
        return "maliciousFile";
    }
    return errorText != SERVER_UNAVAILABLE_ERROR_MESSAGE ? "errorcircle" : 'reset';
};

const mergeRemovingUploadedFiles = (currentUploadedFiles, newUploadedData) => {
    const combinedFile = [...newUploadedData];
    const progressFile = currentUploadedFiles.filter((item) => item.hasOwnProperty('isLoading') && item.isLoading);
    if (progressFile.length > 1) {
        progressFile.forEach((item) => combinedFile.push(item));
    } else if (progressFile.length > 0) {
        combinedFile.push(progressFile[0]);
    }
    return combinedFile;
};

const buildUpdatedFileItem = (item, result) => ({
    "guid": result.guid || item.guid,
    "name": result.file_name || item.name,
    "fileName": result.file_name || item.name,
    "size": item.size,
    "caption": result.caption || "",
    "url": item.url
});

const buildUpdatedItem = (serverResponse, serverResult) => {
    if (Array.isArray(serverResponse)) {
        return serverResponse.map((item, i) => buildUpdatedFileItem(item, serverResult[i] || {}));
    }
    return {
        "guid": serverResult.guid || serverResponse.guid,
        "name": serverResult.file_name || (serverResponse.title || serverResponse.name),
        "fileName": serverResult.file_name || serverResponse.name,
        "size": serverResponse.size,
        "caption": serverResult.caption || "",
        "url": serverResponse.url,
        uploadDate: serverResponse.uploadDate,
    };
};

const findUpdateIndex = (currentUploadedFiles, serverResponse, uploadedFilesId, isSingleFile) => {
    if (!isSingleFile) {
        return currentUploadedFiles.findIndex((item) => item.id == uploadedFilesId);
    }
    const matchName = Array.isArray(serverResponse) ? serverResponse[0].name : serverResponse.name;
    return currentUploadedFiles.findIndex((item) => item.fileName === matchName);
};

const applyUpdatedItem = (el, updatedItem) => {
    const source = Array.isArray(updatedItem) ? updatedItem[0] : updatedItem;
    return {
        ...el,
        guid: source.guid,
        name: source.name,
        fileName: source.fileName,
        size: source.size,
        caption: source.caption,
        url: source.url,
    };
};

const ExzeoUpload = React.forwardRef((props, ref) => {
    const [uploadedFile, setUploadedFile] = useState([]);
    const [errorMessages, setErrorMessages] = useState([]);
    const [completedUploadedFile, setCompletedUploadedFile] = useState(0);

    const filesRef = useRef([]);
    const uploadedFilesRef = useRef(uploadedFile);
    const errorMessageRef = useRef(errorMessages);
    const setUploadedFiles = (value) => {
        uploadedFilesRef.current = value;
        setUploadedFile(value);
    };
    const setErrorMessage = (value) => {
        errorMessageRef.current = value;
        setErrorMessages(value);
    };

    useEffect(() => {
        setUploadedFiles(props.uploadedData);
    }, []);

    const isFirstUpdate = useRef(true);
    const prevUploadedDataRef = useRef(props.uploadedData);
    useEffect(() => {
        if (isFirstUpdate.current) {
            isFirstUpdate.current = false;
            prevUploadedDataRef.current = props.uploadedData;
            return;
        }

        const prevUploadedData = prevUploadedDataRef.current;
        const newUploadedData = props.uploadedData;
        prevUploadedDataRef.current = newUploadedData;

        if (newUploadedData !== null && prevUploadedData !== null && prevUploadedData && prevUploadedData.length !== null
            && prevUploadedData.length !== newUploadedData.length) {
            const totalFileUploaded = uploadedFilesRef.current.filter(isUploadComplete).length;
            const newUploadedFiles = props.removing
                ? mergeRemovingUploadedFiles(uploadedFilesRef.current, newUploadedData)
                : [...uploadedFilesRef.current, ...newUploadedData];
            setUploadedFiles(newUploadedFiles);
            setCompletedUploadedFile(newUploadedData.length != totalFileUploaded ? newUploadedData.length : completedUploadedFile);

            if (newUploadedData?.length) {
                props.saveAndUpdateFileCaption(newUploadedFiles.filter(isUploadComplete));
            }
            props.removing && props.setFalseRemovingState();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.uploadedData]);

    const isValidFile = (fileName) => !props.inValidFiles(fileName.split('.').pop().toLowerCase());

    // @@ size in bytes
    const isValidSize = (size) => size < props.maxSize;

    const GetFileSizeRounded = (fileSize) => {
        let fs = +fileSize;
        if (!Number.isNaN(fs)) {
            //when size is more than 1 MB
            if (fs >= 1048576) {
                fs = fs / 1048576;
                return Math.round(fs) + " MB";
            }
            //when size is more than 1 KB
            else if (fs >= 1024) {
                fs = fs / 1024;
                return Math.round(fs) + " KB";
            }

            //when size is less than  1 KB
            return Math.round(fs) + " bytes";
        }
        return "";
    }

    const checkFilesValidation = (filesdata) => {
        let errorType;
        if (filesdata) {
            if (!isValidFile(filesdata.name)) {
                errorType = INVALID_FILE_EXTENSION;
            } else if (!isValidSize(filesdata.size)) {
                errorType = INVALID_FILE_SIZE;
            } else {
                errorType = VALID_FILE;
            }
        }

        return errorType;
    }

    const onRemoveError = (errorItem) => {
        const filterError = errorMessages.filter((item) => {
            if (item.hasOwnProperty('id')) {
                return item.id !== errorItem
            }
            else {
                return item.name !== errorItem
            }
        })

        setErrorMessage(filterError);
    }

    const changeDiaryCaption = (data) => {
        const updatedCaptiondata = uploadedFile.map((item) => {
            if (item['guid'] === data['guid']) {
                return data;
            }
            return item;
        })
        setUploadedFiles(updatedCaptiondata);
        props.saveAndUpdateFileCaption(updatedCaptiondata);
    }

    const onRemoveFile = (e) => {
        if (e.currentTarget.dataset.currentitem) {
            const currentItem = e.currentTarget.dataset.currentitem;
            const index = props.isSingleFile
                ? filesRef.current.findIndex((item) => item.fileName == currentItem)
                : filesRef.current.findIndex((item) => item.id == currentItem);
            filesRef.current.splice(index, 1);

            // Need to delete from api
            setUploadedFiles([]);
            setCompletedUploadedFile(0);
            props.saveAndUpdateFileCaption([]);
            props.onRemoveFile([]);
            return;
        }

        const removeThis = e.currentTarget.dataset.guid;
        const index = filesRef.current.findIndex((item) => item.name === e.currentTarget.dataset.image);
        filesRef.current.splice(index, 1);
        const currentSelectedFiles = [...uploadedFilesRef.current];
        const filteredFiles = currentSelectedFiles.filter((item) => item.guid !== removeThis);
        const totalFileUploaded = filteredFiles.filter(isUploadComplete);

        setUploadedFiles(filteredFiles);
        setCompletedUploadedFile(totalFileUploaded.length);
        props.saveAndUpdateFileCaption(totalFileUploaded);
        props.onRemoveFile(totalFileUploaded);
    }

    const addFileToList = (serverResponse, serverResult, uploadedFilesId) => {
        const currentUploadedFiles = uploadedFilesRef.current;
        const updateFileUploadedData = [...currentUploadedFiles];
        const updatedItem = buildUpdatedItem(serverResponse, serverResult);
        const index = findUpdateIndex(currentUploadedFiles, serverResponse, uploadedFilesId, props.isSingleFile);

        updateFileUploadedData[index] = { ...updateFileUploadedData[index], isLoading: !updateFileUploadedData[index].isLoading }
        const newUploadedFiles = updateFileUploadedData.map((el, i) =>
            (!el.isLoading && i == index) ? applyUpdatedItem(el, updatedItem) : el
        );

        const totalFileUploaded = newUploadedFiles.filter(isUploadComplete);

        setUploadedFiles(newUploadedFiles);
        setCompletedUploadedFile(props.isSingleFile ? 1 : totalFileUploaded.length);
        props.saveAndUpdateFileCaption(totalFileUploaded);
    }

    const handleUploadFailure = (file, errorText) => {
        const currentError = { id: file.id, name: file.name, error: errorText };
        const removeErrorFile = uploadedFilesRef.current.filter(e => e.id != file.id);
        setUploadedFiles(removeErrorFile);
        setErrorMessage([...errorMessageRef.current, currentError]);
    }

    const uploadOnPublicApi = async (files) => {
        for (const file of files) {
            const formData = new FormData();
            formData.append('file', file, file.name);
            filesRef.current.push(file);
            try {
                const res = await uploadFileOnPublicApiV2(formData);

                if (res.status == 200) {
                    const result = res.data.result[0];
                    file.guid = result.guid;
                    if (props.isSingleFile) {
                        addFileToList(files, [result]);
                    } else {
                        addFileToList(file, result, file.id);
                    }
                } else {
                    handleUploadFailure(file, res.statusText);
                }
            } catch (error) {
                handleUploadFailure(file, getAxiosError(error)?.error || SERVER_UNAVAILABLE_ERROR_MESSAGE);
            }
        }
    }

    const buildSingleFileValidationError = (file, isValidFiles) => {
        if (isValidFiles === INVALID_FILE_EXTENSION) {
            return { name: file.name, error: INVALID_FILE_EXTENSION_ERROR };
        }
        if (isValidFiles === INVALID_FILE_SIZE) {
            return { name: file.name, error: INVALID_FILE_SIZE_ERROR + ' ' + GetFileSizeRounded(props.maxSize) };
        }
        return undefined;
    }

    const buildMultiFileValidationError = (errorId, file, isValidFiles) => {
        if (isValidFiles === INVALID_FILE_EXTENSION) {
            return { id: errorId, name: file.name, error: INVALID_FILE_EXTENSION_ERROR };
        }
        if (isValidFiles === INVALID_FILE_SIZE) {
            return { id: errorId, name: file.name, error: INVALID_FILE_SIZE_ERROR + ' ' + GetFileSizeRounded(props.maxSize) };
        }
        return undefined;
    }

    const uploadFile = (files) => {
        let validfile = [];
        let _progressInfo = props.isSingleFile ? [] : [...uploadedFilesRef.current];
        if (props.isSingleFile) {
            const isValidFiles = checkFilesValidation(files[0]);
            if (isValidFiles === VALID_FILE) {
                const localUrl = isImageFile(files[0].name) ? URL.createObjectURL(files[0]) : undefined;
                _progressInfo.unshift({ progress: 0, name: files[0].name, fileName: files[0].name, isLoading: true, localUrl });
                validfile.push(files[0]);
            }
            else {
                setErrorMessage([...errorMessages, buildSingleFileValidationError(files[0], isValidFiles)]);
            }
        }
        else {
            for (const file of files) {
                const isValidFiles = checkFilesValidation(file);
                if (isValidFiles === VALID_FILE) {
                    const id = size(_progressInfo) + 1;
                    const localUrl = isImageFile(file.name) ? URL.createObjectURL(file) : undefined;
                    _progressInfo.unshift({ id: id, progress: 0, name: file.name, fileName: file.name, isLoading: true, localUrl });
                    file.id = id;
                    validfile.push(file);
                }
                else {
                    const errorId = size(errorMessages) + 1;
                    setErrorMessage([...errorMessages, buildMultiFileValidationError(errorId, file, isValidFiles)]);
                }
            }
        }
        if (validfile.length > 0) {
            setUploadedFiles(_progressInfo);
            uploadOnPublicApi(validfile);
        }
    }

    const handleselectedFile = (event) => {
        uploadFile(event.target.files);
    }

    React.useImperativeHandle(ref, () => ({
        uploadFile,
    }));

    const renderUploadControl = () => {
        return (

            <div className={"rowFlex uploadButton"} role={"button"}>
                <span className={classNames("rowFlex alignMiddle", props.styles)}>
                    <button type='button' className="btn-cc__link btn-cc__link__blue mrNone">
                        <Icons name="attach" className="icon mr" />{props.isSingleFile ? "Attach file" : ATTACH_FILES}
                    </button>

                    <input
                        type="file"
                        title={props.isSingleFile ? "" : ATTACH_FILES}
                        name={'fileName'}
                        multiple={props.multiple || true}
                        className={'fileupload'}
                        onChange={handleselectedFile}
                        onClick={(event) => event.currentTarget.value = ''}
                    />
                    {
                        (props.openAttachedWorkardFiles)
                            &&
                            <>
                            <button type='button' className='btn-cc__link btn-cc__link__blue mrNone' onClick={props.openAttachedWorkardFiles}> {ATTACH_WORKKARD_FILES}</button>
                            </>
                    }
                    {
                        (props.openAttachedLetterFiles)
                            &&
                            <>
                            <button type='button' className='btn-cc__link btn-cc__link__blue' onClick={props.openAttachedLetterFiles}> {ATTACH_WORKKARD_LETTER}</button>
                            </>
                    }
                </span>
                {props.helpText && <span className="quickTooltip-top" data-quicktooltip={props.helpText}><Icons name="info" className="icon ml greyInfoIcon" /></span>}
            </div>
        )
    }

    return (
        <div className={"file_uploader hasMultipleFileUpload"}>
            <div>
                {props.isSingleFile ? renderUploadControl() : (
                    <form encType="multipart/form-data"  >
                        {renderUploadControl()}
                    </form>
                )}
                {
                    ((uploadedFile?.length) || (errorMessages.length))
                        ?
                        (
                            <React.Fragment>
                                <div className="bo mt">
                                    {completedUploadedFile != 0 ? `You have selected ${completedUploadedFile} ${props.isSingleFile ? 'file' : 'file(s)'}.` : ""}
                                </div>
                                <div className={'fileUploadMain'}>
                                    <ul>
                                        {
                                            errorMessages.map((item, index) => {
                                                return (
                                                    <li key={`${item.id || item.name}_${index}`}>
                                                        <div className="uploadImage errorUpload">
                                                            <Icons name={getErrorIconName(item.error)} className="icon" />
                                                        </div>
                                                        <div className="uploadImageInfo">
                                                            <span className="fileName" title={item.name}>{shortFileName(item.name)}</span>
                                                            <span className="errorSelfMsg">{item.error}</span>
                                                            <button type="button" className="iconButtonReset btn-cc__transparent" onClick={() => { onRemoveError(!props.isSingleFile ? item.id : item.name) }}>
                                                                <Icons name="closeFilled" className="icon close" />
                                                            </button>
                                                        </div>
                                                    </li>)
                                            })
                                        }
                                        {
                                            uploadedFile?.map((item, index) => {
                                                return (
                                                    <FileList
                                                        key={`${item.guid || item.id || item.fileName}_${index}`}
                                                        data={item}
                                                        itemIndex={index}
                                                        saveAndUpdateFileCaption={changeDiaryCaption}
                                                        onRemoveFile={onRemoveFile}
                                                        isSingleFile={props.isSingleFile}
                                                        hideCaption={props.hideCaption}
                                                    />
                                                )

                                            })
                                        }
                                    </ul>
                                </div>
                            </React.Fragment>
                        )
                        :
                        (
                            null
                        )
                }
            </div>
        </div>
    );
});

ExzeoUpload.displayName = 'ExzeoUpload';

export default ExzeoUpload;
